import { useEffect, useState } from "react";
import T from "@/i18n/T";

interface CountdownTimerProps {
  endDate: string | null;
}

export function CountdownTimer({ endDate }: CountdownTimerProps) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0 });
  const [isEnded, setIsEnded] = useState(false);

  useEffect(() => {
    if (!endDate) return;

    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const end = new Date(endDate).getTime();
      const difference = end - now;

      if (difference <= 0) {
        setIsEnded(true);
        return;
      }

      setTimeLeft({
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
      });
    };

    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [endDate]);

  if (!endDate) {
    return (
      <div className="flex gap-2">
        <TimeBox value="--" label="DAYS" />
        <TimeBox value="--" label="HRS" />
        <TimeBox value="--" label="MINS" />
      </div>
    );
  }

  if (isEnded) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg">
        <span className="text-sm font-medium text-muted-foreground"><T k="auto.resolving-5e68" /></span>
      </div>
    );
  }

  return (
    <div className="flex gap-2">
      <TimeBox value={timeLeft.days.toString().padStart(2, "0")} label="DAYS" />
      <TimeBox value={timeLeft.hours.toString().padStart(2, "0")} label="HRS" />
      <TimeBox value={timeLeft.minutes.toString().padStart(2, "0")} label="MINS" />
    </div>
  );
}

function TimeBox({ value, label }: { value: string; label: string }) {
  return (
    <div className="flex flex-col items-center bg-secondary rounded-lg px-3 py-2 min-w-[50px]">
      <span className="text-lg font-bold text-foreground">{value}</span>
      <span className="text-[10px] text-muted-foreground font-medium">{label}</span>
    </div>
  );
}
